<template>
  <div class="container-fluid max-container">
    <div class="row">
      <Header />
      <section>
        <div class="container">
          <div class="archaic-upgrade-container-fluid">
            <nav class="mt-4" style="--bs-breadcrumb-divider: '>'" aria-label="breadcrumb">
              <ol class="breadcrumb blood-test-breadcrumb">
                <li class="breadcrumb-item" aria-current="page">
                  <router-link :to="{ name: 'dashboard' }">Home</router-link>
                </li>
                <li class="breadcrumb-item" aria-current="page">
                  <router-link :to="{ name: 'bloodTest' }">Blood Test</router-link>
                </li>
                <li class="breadcrumb-item ">
                  <router-link :to="{ name: 'bloodTest' }">{{ selectedBloodTest?.name }}</router-link>
                </li>
                <li class="breadcrumb-item active" aria-current="page">
                  <router-link :to="{ name: 'bloodTest' }">{{ selectedPanelName }}</router-link>
                </li>
              </ol>
            </nav>
            <div class="prev-button is-mobile">
              <router-link :to="{ name: 'bloodTest' }">
                <img src="@/assets/images/back-arrow.svg" />
                <span>Back</span>
              </router-link>
            </div>

            <div class="report-wrapper-row">
              <div class="row">
                <div class="col-lg-3">
                  <BloodTestPanel :selectedBloodTest="selectedBloodTest" @selectPanel="handlePanelSelection" />
                </div>
                <div class="col-lg-9">
                  <div id="report-wrapper-row" class="report-right-row">
                    <h2>{{ selectedPanelName }}</h2>
                    <div v-for="marker in panels" :key="marker.name" class="urea-content-container">
                      <div v-if="Number.isNaN(marker.result) || !marker.resultRangeStage || !marker.result">
                        <div class="mb-6">
                          <h2 class="font-semibold text-lg flex items-center mb-2">
                            <div class="mr-2">
                              <MarkerIcon :marker="marker" />
                            </div>
                            {{ marker.name }}
                          </h2>
                          <p v-html="markerDetailsMap[marker.code]?.all_about_body"></p>
                        </div>

                        <div class="bg-white rounded-lg shadow p-6">
                          <div v-if="marker.resultDescription" v-html="marker.resultDescription" />
                          <div v-else>Unfortunately testing of this biomarker failed</div>
                        </div>
                      </div>
                      <div v-else>
                        <div class="heading-urea-row">
                          <h2>{{ marker.name }}</h2>
                          <router-link
                            :to="{ name: 'bloodTestTest', params: { marker: marker.code.toLowerCase(), panel: selectedPanelName ? selectedPanelName : 'Genral Details' } }">
                            <p>Find out More <img :src="ArrowRightIcon" alt="" /></p>
                          </router-link>
                        </div>
                        <p v-html="markerDetailsMap[marker.code]?.all_about_body"></p>

                        <!-- Tabs for Result and Trend -->
                        <div class="active-tab-urea-row">
                          <div
                            :class="{ 'active active-graph': activeTabMap[marker.code] === 'Result' && marker.result }"
                            @click="changeTab('Result', marker.code)">
                            <p>Result</p>
                          </div>
                          <div
                            :class="{ 'active active-graph': activeTabMap[marker.code] === 'Trend' && marker.result }"
                            @click="changeTab('Trend', marker.code)">
                            <p>Trend</p>
                          </div>
                        </div>

                        <!-- Conditionally render LiverFunction component based on the marker's active tab -->
                        <LiverFunction v-if="isTrendTabActive(marker.code)" :marker="marker"
                          :selected-blood-test="selectedBloodTest" />
                        <div class="progress-bar-container-grid-row" v-else>
                          <div>
                            <MarkerResultPill :result-percentage="resultAsPecentage(marker)" :color-map="colorMap"
                              :stage-width="stageWidth" :marker="marker" />
                            <h4>
                              {{
                                ["rangeLowNormal", "rangeNormal",
                                  "rangeHighNormal"].includes(marker.resultRangeStage?.name)
                                  ? "Result in range"
                                  : "Result out of range"
                              }}
                            </h4>
                            <p>(Reference range {{ marker.resultRange }} {{ marker.resultUnit }})</p>
                          </div>
                          <div>
                            <p v-html="marker.resultDescription"></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import Header from "@/components/header/Header.vue";
import "../../../styles/pages/FunctionEfficiency.scss";
import ArrowRightIcon from "../../../assets/arrow-right-02-round.png";
import { ref, computed, reactive, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePatientStore } from "@/store/health/blood";
import MarkerResultPill from "./MarkerResultPill.vue";
import LiverFunction from "./LiverFunction.vue";
import BloodTestPanel from "./BloodTestPanel.vue";
import { getMarkerDetails } from "@/store/marker";
import { nextTick } from "vue";
import { RESULT_COLORS } from "@/styles/base/resultColors";

// In range is good, the high and low ends of the range are normal, out of range is not so good.
const colorMap = {
  rangeLow: RESULT_COLORS.poor,
  rangeLowNormal: RESULT_COLORS.normal,
  rangeNormal: RESULT_COLORS.good,
  rangeHighNormal: RESULT_COLORS.normal,
  rangeHigh: RESULT_COLORS.poor,
};

const route = useRoute();
const patientStore = usePatientStore();
const selectedPanelName = ref('');
const activeTabMap = reactive({});
const router = useRouter();

onMounted(() => {
  selectedPanelName.value = route.params.panel ? route.params.panel : '';
});

const changeTab = (tab, markerCode) => {
  if (!activeTabMap[markerCode]) {
    activeTabMap[markerCode] = 'Result';
  }
  activeTabMap[markerCode] = tab;
};

const isTrendTabActive = (markerCode) => {
  return activeTabMap[markerCode] === 'Trend';
};

const selectedBloodTest = patientStore.getTestByBarcode(route.params.barcode);

const handlePanelSelection = (panelName, barcode) => {
  selectedPanelName.value = panelName;
  // router.push(`/kidney/electrolytes/${barcode}/${panelName}`);
  router.push(`/kidney/electrolytes/${barcode}/${panelName}`).then(() => {
    nextTick(() => {
      const section = document.getElementById("report-wrapper-row");
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    });
  });
};

const panels = computed(() => {
  const filteredPanels = selectedBloodTest.result.panels.filter(panel => panel.name === route.params.panel);
  const sortedPanels = [...filteredPanels].sort((a) =>
    getLowestRangeForPanel(a) === "rangeLow" ? -1 : 1
  );
  const markers = sortedPanels.flatMap(panel => panel.markers);
  markers.forEach(marker => {
    if (!activeTabMap[marker.code]) {
      activeTabMap[marker.code] = 'Result';
    }
    fetchMarkerDetails(marker.code);
  });
  return markers;
});
const markerDetailsMap = ref({});

const fetchMarkerDetails = (code) => {
  const details = getMarkerDetails(code);
  markerDetailsMap.value[code] = details;
};

function getLowestRangeForPanel(panel) {
  const stages = panel.markers?.map((marker) => marker?.resultRangeStage?.name) || [];
  if (stages.includes("rangeLow") || stages.includes("rangeHigh")) return "rangeLow";
  if (stages.includes("rangeLowNormal") || stages.includes("rangeHighNormal")) return "rangeLowNormal";
  return "rangeNormal";
}

function stageWidth(stage, marker) {
  return Math.ceil(
    ((stage.highest - stage.lowest) /
      (marker.range.highestPossible - marker.range.lowestPossible)) *
    100
  );
}

function resultAsPecentage(result) {
  const value = result.result;
  return (
    ((value - result.range.lowestPossible) /
      (result.range.highestPossible - result.range.lowestPossible)) *
    100
  );
}
</script>

<style scoped>
.progress-bar-wrapper {
  width: 100%;
  margin: 20px 0;
}

.progress-barx {
  position: relative;
  height: 40px;
  display: flex;
  flex-direction: row;
}

.low-range,
.high-range,
.normal-range {
  height: 100%;
}

.low-range {
  border-radius: 50px 0px 0px 50px;
  background-color: #ff5722;
  width: 20%;
}

.normal-range {
  background-color: #4caf50;
  width: 60%;
}

.high-range {
  border-radius: 0px 50px 50px 0px;
  background-color: #ff5722;
  width: 20%;
}

.marker {
  position: absolute;
  top: -5px;
  width: 2px;
  height: 45px;
  background-color: black;
  transform: translateX(-50%);
}

.value {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  border-radius: 3px;
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.p-6 {
  padding: 1.5rem;
}

.value>p {
  font-weight: 500;
  font-size: 14px;
}
</style>
